<template>
  <div id="app" :class="{ 'dark-mode': isDarkMode }">
    <div v-if="showSplash" class="warehouse-splash-screen">
      <div class="warehouse-splash-backdrop"></div>
      <div class="warehouse-splash-card">
        <img :src="splashLogo" alt="Pavicon ERP" class="warehouse-splash-logo" />
        <div class="warehouse-splash-progress">
          <span class="warehouse-splash-progress-bar"></span>
        </div>
      </div>
    </div>
    <Header :mainHeader="true" v-if="showHeader" />
    <router-view v-slot="{ Component }" v-if="!showSplash">
      <Transition name="page" mode="out-in">
        <component :is="Component" :key="$route.path" />
      </Transition>
    </router-view>
  </div>
</template>

<script>

import { darkModeState } from './composables/useDarkMode'
import Header from './components/Header.vue'
import splashLogo from './assets/images/ppt/pavicon_login_ppt.png'

export default {
  name: 'App',
  components: {
    Header
  },
  data() {
    return {
      splashLogo,
      showSplash: false,
      splashTimer: null,
    }
  },
  computed: {
    isDarkMode() {
      return darkModeState.isDarkMode
    },
    isLoginRoute() {
      const path = this.$route?.path || ''
      return path === '/' || path === '/login' || path === '/auth' || path === '/auth/login'
    },
    isAuthPage() {
      // Hide header on auth pages (e.g., login, register, forgot-password)
      const authRoutes = ['/', '/login', '/auth', '/auth/login', ];
      // Hide header for auth pages or routes explicitly marked public
      return authRoutes.includes(this.$route.path) || this.$route.path.startsWith('/auth') || !!this.$route.meta?.public;
    }
    ,
    showHeader() {
      const authRoutes = ['/', '/login', '/auth', '/auth/login'];
      const p = this.$route?.path || '';
      // Procurement dashboard uses normal layout with header
      if (p === '/admin/procurement/analytics') return true;
      const activeModule = localStorage.getItem('active_module') || '';
      if (activeModule === 'procurement') return false;
      if (p.startsWith('/procurement') || p.startsWith('/admin/procurement')) return false;
      return !(authRoutes.includes(p) || p.startsWith('/auth') || !!this.$route.meta?.public || p.includes('pdf'));
    }
  },
  mounted() {
    this.syncSplashState()
  },
  beforeUnmount() {
    this.clearSplashTimer()
  },
  watch: {
    '$route.path'() {
      this.syncSplashState()
    }
  },
  methods: {
    clearSplashTimer() {
      if (this.splashTimer) {
        window.clearTimeout(this.splashTimer)
        this.splashTimer = null
      }
    },
    syncSplashState() {
      this.clearSplashTimer()

      if (!this.isLoginRoute) {
        this.showSplash = false
        return
      }

      const hasSeenSplash = sessionStorage.getItem('warehouse_login_splash_seen') === 'true'
      if (hasSeenSplash) {
        this.showSplash = false
        return
      }

      this.showSplash = true
      this.splashTimer = window.setTimeout(() => {
        this.showSplash = false
        sessionStorage.setItem('warehouse_login_splash_seen', 'true')
        this.splashTimer = null
      }, 4000)
    }
  },
  meta:{
    title: 'Default Title',
    titleTemplate: '%s | My Awesome Webapp'
  }
}
</script>

<style>
@import './assets/css/dark-mode.css';

#app {
  min-height: 100vh;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  transition: background-color 0.3s ease, color 0.3s ease;
}

/* Page transition animations */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.22s ease-out, transform 0.22s ease-out;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(6px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Override for HTML and body to ensure full page coverage */
html, body {
  margin: 0;
  padding: 0;
  min-height: 100vh;
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

.warehouse-splash-screen {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 24px;
}

.warehouse-splash-backdrop {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at top left, rgba(236, 118, 18, 0.18), transparent 34%),
    radial-gradient(circle at bottom right, rgba(29, 57, 133, 0.22), transparent 38%),
    linear-gradient(135deg, #f8faff 0%, #eef3fb 48%, #fef5eb 100%);
}

.warehouse-splash-card {
  position: relative;
  width: min(320px, calc(100vw - 32px));
  padding: 10px 12px 8px;
  text-align: center;
}

.warehouse-splash-logo {
  width: min(120px, 100%);
  height: auto;
  margin: 0 auto 12px;
  display: block;
}

.warehouse-splash-progress {
  width: min(180px, 100%);
  height: 6px;
  margin: 0 auto;
  border-radius: 999px;
  background: rgba(29, 57, 133, 0.08);
  overflow: hidden;
}

.warehouse-splash-progress-bar {
  display: block;
  width: 45%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #1d3985, #ec7612);
  animation: warehouseSplashProgress 1.8s ease-in-out infinite;
}

@keyframes warehouseSplashProgress {
  0% {
    transform: translateX(-110%);
  }
  55% {
    transform: translateX(130%);
  }
  100% {
    transform: translateX(130%);
  }
}

@media (max-width: 640px) {
  .warehouse-splash-card {
    width: min(260px, calc(100vw - 24px));
    padding: 8px 10px 6px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .warehouse-splash-progress-bar,
  .page-enter-active,
  .page-leave-active {
    animation: none !important;
    transition: none !important;
  }
}
</style>
<style>
/* In procurement mode, hide the main navigation links but keep header (user menu, notifications) visible */
.procurement-mode .menu.main-nav {
  display: none !important;
}

/* also hide any top-level Business Development nav dropdown when in procurement mode */
.procurement-mode .menu.main-nav + .menu-dropdown,
.procurement-mode .menu.menu-dropdown {
  display: none !important;
}
</style>
